interface GeocodeResult {
  lat: number;
  long: number;
}

// Cache for geocoding results to avoid repeated API calls
const geocodeCache = new Map<string, GeocodeResult | null>();

/**
 * Convert a US zip code to coordinates using Zippopotam.us API (free, no key, CORS-friendly)
 * @param zipCode - 5-digit US zip code
 * @returns Promise<GeocodeResult | null> - Coordinates or null if not found
 */
export async function geocodeZipCode(
  zipCode: string
): Promise<GeocodeResult | null> {
  if (!/^\d{5}$/.test(zipCode.trim())) return null;

  const cleanZipCode = zipCode.trim();
  if (geocodeCache.has(cleanZipCode)) return geocodeCache.get(cleanZipCode) || null;

  try {
    const response = await fetch(`https://api.zippopotam.us/us/${cleanZipCode}`);
    if (!response.ok) {
      geocodeCache.set(cleanZipCode, null);
      return null;
    }

    const data = await response.json();
    if (data?.places?.[0]) {
      const place = data.places[0];
      const result: GeocodeResult = {
        lat: parseFloat(place.latitude),
        long: parseFloat(place.longitude),
      };
      if (!isNaN(result.lat) && !isNaN(result.long)) {
        geocodeCache.set(cleanZipCode, result);
        return result;
      }
    }

    geocodeCache.set(cleanZipCode, null);
    return null;
  } catch (error) {
    console.error(`Error geocoding zip code ${cleanZipCode}:`, error);
    geocodeCache.set(cleanZipCode, null);
    return null;
  }
}

/**
 * Geocode a full address using Nominatim (server-side fallback)
 * For zip codes, prefer geocodeZipCode() which uses Zippopotam.us
 */
export async function geocodeAddress(
  address: string
): Promise<GeocodeResult | null> {
  if (!address?.trim()) return null;

  const cleanAddress = address.trim();
  if (geocodeCache.has(cleanAddress)) return geocodeCache.get(cleanAddress) || null;

  // If the address looks like a zip code, use the zip endpoint
  const zipMatch = cleanAddress.match(/\b(\d{5})\b/);
  if (zipMatch) {
    const result = await geocodeZipCode(zipMatch[1]);
    if (result) {
      geocodeCache.set(cleanAddress, result);
      return result;
    }
  }

  geocodeCache.set(cleanAddress, null);
  return null;
}

/**
 * Clear the geocoding cache (useful for testing or memory management)
 */
export function clearGeocodeCache(): void {
  geocodeCache.clear();
}

/**
 * Get the current size of the geocoding cache
 */
export function getGeocodeCache(): ReadonlyMap<string, GeocodeResult | null> {
  return geocodeCache;
}

const zipPlaceCache = new Map<string, Promise<string | null>>();

/** "59044" → "Laurel, MT" via Zippopotam.us; null when unknown. Cached per ZIP. */
export function lookupZipPlace(zipCode: string): Promise<string | null> {
  const clean = zipCode.trim().slice(0, 5);
  if (!/^\d{5}$/.test(clean)) return Promise.resolve(null);
  let pending = zipPlaceCache.get(clean);
  if (!pending) {
    pending = fetch(`https://api.zippopotam.us/us/${clean}`)
      .then(async (response) => {
        if (!response.ok) return null;
        const place = (await response.json())?.places?.[0];
        const city = typeof place?.["place name"] === "string" ? place["place name"].trim() : "";
        const state = typeof place?.["state abbreviation"] === "string" ? place["state abbreviation"].trim() : "";
        return city ? (state ? `${city}, ${state}` : city) : null;
      })
      .catch(() => null);
    zipPlaceCache.set(clean, pending);
  }
  return pending;
}
